import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useFunction } from '@/hooks/useFunction';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Busy, ConfirmDelete, Field, PageHeader, Panel } from '@/components/kit';
import { FormDialog, ListBody, PickSelect } from '@/components/inputs';
import VerifierResult from '@/components/verifier/VerifierResult';
import { planName } from '@/shared/pricing';
import { committed, logActivity, rows } from '@/shared/data';
import { CODE_PATTERN, CODE_PREFIX, asOrderCode } from '@/shared/orderCode';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

const { eq } = window.ApperSDK.CoreSDK;

export const route = {
  path: '/admin/verifier',
  layout: 'owner',
  access: "['Admin_c','Staff_c'].includes(profile)",
};
export const nav = {
  icon: 'ShieldCheck',
  label: 'IPTV Verifier',
  section: 'Store',
  profiles: ['Admin_c', 'Staff_c'],
  order: 43,
};

const VERIFIER = import.meta.env.VITE_IPTV_VERIFIER;
const PLAN_KEYS = ['Wraith', 'Demon', 'Banshee'];
const NO_PLAN = 'none';
const PLAN_OPTIONS = [NO_PLAN, ...PLAN_KEYS];
const PLAN_LABELS = { [NO_PLAN]: 'No plan', ...Object.fromEntries(PLAN_KEYS.map((p) => [p, planName(p)])) };
const MAX_LINKS = 25;

const splitLinks = (text) =>
  Array.from(
    new Set(
      String(text || '')
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );

function hostOf(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  try {
    return new URL(/^https?:\/\//i.test(v) ? v : `http://${v}`).host;
  } catch {
    return v;
  }
}

/** References joined with their team-only names and links. */
async function loadReferences() {
  const [refs, details] = await Promise.all([
    sdk
      .table('line_references_c')
      .select(['Name', 'plan_c', 'stream_count_c', 'category_count_c', 'captured_at_c'])
      .orderByDesc('captured_at_c')
      .limit(100)
      .fetch()
      .then(rows),
    sdk
      .table('reference_links_c')
      .select(['Name', 'reference_id_c', 'server_c', 'links_c'])
      .limit(200)
      .fetch()
      .then(rows),
  ]);
  const byRef = new Map(details.map((d) => [String(d.reference_id_c), d]));
  return refs.map((r) => {
    const d = byRef.get(String(r.Id));
    const legacyPlan = PLAN_KEYS.includes(r.Name) ? r.Name : null;
    const plan = PLAN_KEYS.includes(r.plan_c) ? r.plan_c : legacyPlan;
    return {
      ...r,
      detail: d ?? null,
      label: d?.Name || (legacyPlan ? planName(legacyPlan) : r.Name),
      plan,
      server: d?.server_c ?? '',
      links: splitLinks(d?.links_c),
    };
  });
}

function LinksField({ id, value, onChange }) {
  const count = splitLinks(value).length;
  return (
    <Field
      label="Extra links"
      htmlFor={id}
      hint={`Backup servers, alternate DNS names or portal URLs for the same service, one per line. ${count} of ${MAX_LINKS}. Matched by hash; customers never see them.`}
    >
      <Textarea
        id={id}
        rows={4}
        value={value}
        onChange={onChange}
        placeholder={'http://backup.example.com:8080\nalt-dns.example.net\nportal.example.org'}
        className="font-mono text-xs"
      />
    </Field>
  );
}

function NamePlanFields({ prefix, form, set, setPlan }) {
  return (
    <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
      <Field label="Name" htmlFor={`${prefix}-name`} hint="Only the team sees this.">
        <Input
          id={`${prefix}-name`}
          value={form.name}
          onChange={set('name')}
          placeholder="Wraith main server"
          maxLength={60}
          required
        />
      </Field>
      <Field label="Plan" htmlFor={`${prefix}-plan`} hint="Optional.">
        <PickSelect
          id={`${prefix}-plan`}
          value={form.plan}
          onChange={setPlan}
          values={PLAN_OPTIONS}
          labels={PLAN_LABELS}
          placeholder="No plan"
        />
      </Field>
    </div>
  );
}

/** Add a new reference, or capture an existing one again. */
function CaptureDialog({ reference, open, onOpenChange, onSaved }) {
  const blank = { name: '', plan: NO_PLAN, server: '', username: '', password: '', links: '' };
  const [form, setForm] = useState(blank);
  const capture = useFunction(VERIFIER);
  const linker = useFunction(VERIFIER, { showError: false });

  useEffect(() => {
    if (!open) return;
    setForm(
      reference
        ? {
            name: reference.label,
            plan: reference.plan ?? NO_PLAN,
            server: reference.server,
            username: '',
            password: '',
            links: reference.links.join('\n'),
          }
        : blank,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reference]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setPlan = (v) => setForm((f) => ({ ...f, plan: v }));

  async function submit() {
    const name = form.name.trim();
    const links = splitLinks(form.links);
    if (name.length < 2) return toast.error('Give the reference a name');
    if (!form.server.trim() || !form.username.trim() || !form.password.trim()) {
      return toast.error('Server, username and password are all needed for the scan');
    }
    if (links.length > MAX_LINKS) return toast.error(`Up to ${MAX_LINKS} extra links`);
    const plan = form.plan === NO_PLAN ? '' : form.plan;
    const res = await capture.invoke({
      mode: 'reference',
      referenceId: reference?.Id,
      name,
      plan,
      server: form.server.trim(),
      username: form.username.trim(),
      password: form.password,
      links,
    });
    setForm((f) => ({ ...f, password: '' }));
    if (!res) return;

    // Belt and braces: if the name and links did not land, attach them to the row just captured.
    let detailsOk = res.detailsSaved !== false;
    if (!detailsOk) {
      let targetId = res.referenceId;
      if (!targetId) {
        try {
          const latest = await sdk
            .table('line_references_c')
            .select(['Name', 'captured_at_c'])
            .orderByDesc('captured_at_c')
            .limit(1)
            .fetch();
          targetId = latest?.data?.[0]?.Id;
        } catch {
          targetId = undefined;
        }
      }
      if (targetId) {
        detailsOk = !!(await linker.invoke({ mode: 'links', referenceId: targetId, name, plan, links }));
      }
    }

    logActivity('Verifier', reference ? 'Captured reference again' : 'Added reference', name, plan || '');
    toast.success(`${name} saved`, {
      description: `${res.scan?.liveChannels ?? 0} live channels fingerprinted. Self-check score ${res.selfScore}.`,
    });
    if (!detailsOk) toast.warning('The fingerprint is saved, but the name and links were not. Use Edit on the card to set them.');
    onOpenChange(false);
    onSaved();
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={reference ? `Capture ${reference.label} again` : 'Add a reference'}
      description="Use a known-good line. We sign in once, keep a fingerprint of the server and channel list, and throw the password away."
      onSubmit={submit}
      saving={capture.loading || linker.loading}
      submitLabel="Capture fingerprint"
      busyLabel="Scanning the line…"
      wide
    >
      <NamePlanFields prefix="ref" form={form} set={set} setPlan={setPlan} />
      <Field label="Server address" htmlFor="ref-server" hint="The host and port from the line, with or without http://.">
        <Input
          id="ref-server"
          value={form.server}
          onChange={set('server')}
          placeholder="http://line.example.com:8080"
          autoComplete="off"
          spellCheck={false}
          required
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Username" htmlFor="ref-username">
          <Input
            id="ref-username"
            value={form.username}
            onChange={set('username')}
            placeholder="ghost123"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </Field>
        <Field label="Password" htmlFor="ref-password" hint="Used for this scan only. Never saved.">
          <Input
            id="ref-password"
            type="password"
            value={form.password}
            onChange={set('password')}
            placeholder="Line password"
            autoComplete="new-password"
            required
          />
        </Field>
      </div>
      <LinksField id="ref-links" value={form.links} onChange={set('links')} />
      {capture.loading && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          Reading the channel list. Big lineups take up to a minute.
        </p>
      )}
    </FormDialog>
  );
}

/** Rename a reference, change its plan, or edit its extra links without a new scan. */
function EditDialog({ reference, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: '', plan: NO_PLAN, links: '' });
  const editor = useFunction(VERIFIER);

  useEffect(() => {
    if (open && reference) {
      setForm({ name: reference.label, plan: reference.plan ?? NO_PLAN, links: reference.links.join('\n') });
    }
  }, [open, reference]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setPlan = (v) => setForm((f) => ({ ...f, plan: v }));

  async function submit() {
    const name = form.name.trim();
    const links = splitLinks(form.links);
    if (name.length < 2) return toast.error('Give the reference a name');
    if (links.length > MAX_LINKS) return toast.error(`Up to ${MAX_LINKS} extra links`);
    const plan = form.plan === NO_PLAN ? '' : form.plan;
    const res = await editor.invoke({ mode: 'links', referenceId: reference.Id, name, plan, links });
    if (!res) return;
    logActivity('Verifier', 'Edited reference', name, `${links.length} links`);
    toast.success(`${name} saved`, { description: `${res.hostCount ?? links.length + 1} server addresses now match.` });
    onOpenChange(false);
    onSaved();
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Edit ${reference?.label ?? 'reference'}`}
      description="Changes the name, plan and extra links. The channel fingerprint stays as captured."
      onSubmit={submit}
      saving={editor.loading}
      submitLabel="Save"
      wide
    >
      <NamePlanFields prefix="edit" form={form} set={set} setPlan={setPlan} />
      <LinksField id="edit-links" value={form.links} onChange={set('links')} />
    </FormDialog>
  );
}

function ReferenceCard({ reference: r, onCapture, onEdit, onDeleted }) {
  const [showLinks, setShowLinks] = useState(false);

  async function remove() {
    const res = await sdk.table('line_references_c').remove(r.Id);
    if (!committed(res, `${r.label} deleted`)) return false;
    if (r.detail?.Id) {
      try {
        await sdk.table('reference_links_c').remove(r.detail.Id);
      } catch {
        // the fingerprint is gone, which is what matters
      }
    }
    logActivity('Verifier', 'Deleted reference', r.label);
    onDeleted();
    return true;
  }

  return (
    <div className="flex flex-col rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-heading text-xl leading-tight" title={r.label}>
            {r.label}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {r.plan ? (
              <Badge variant="outline">{planName(r.plan)}</Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                No plan
              </Badge>
            )}
            {r.links.length > 0 && (
              <Badge variant="outline" className="text-muted-foreground">
                +{r.links.length} link{r.links.length === 1 ? '' : 's'}
              </Badge>
            )}
          </div>
        </div>
        <ApperIcon name="CircleCheck" size={18} className="mt-1 shrink-0 text-success" />
      </div>

      <div className="mt-3 flex-1 space-y-1 text-sm">
        <p className="text-muted-foreground" title={formatLocalDate(r.captured_at_c, DATE_FORMATS.WITH_TIME)}>
          Captured {formatRelativeTime(r.captured_at_c)}
        </p>
        <p className="tabular-nums">
          {Number(r.stream_count_c || 0).toLocaleString()} live channels ·{' '}
          {Number(r.category_count_c || 0).toLocaleString()} categories
        </p>
        {r.server && (
          <p className="truncate font-mono text-xs text-muted-foreground" title={r.server}>
            {hostOf(r.server)}
          </p>
        )}
        {r.links.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowLinks((s) => !s)}
              aria-expanded={showLinks}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ApperIcon name={showLinks ? 'ChevronUp' : 'ChevronDown'} size={14} />
              {showLinks ? 'Hide links' : 'Show links'}
            </button>
            {showLinks && (
              <ul className="mt-1 space-y-0.5">
                {r.links.map((l) => (
                  <li key={l} className="truncate font-mono text-xs text-muted-foreground" title={l}>
                    {l}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onCapture}>
          <ApperIcon name="RefreshCw" size={16} />
          Capture again
        </Button>
        <Button variant="outline" size="sm" onClick={onEdit}>
          <ApperIcon name="Pencil" size={16} />
          Edit
        </Button>
        <ConfirmDelete
          title={`Delete ${r.label}?`}
          description="Lines are no longer compared against this reference. Verified badges already on orders stay until the next check."
          onConfirm={remove}
        />
      </div>
    </div>
  );
}

function CheckOrder() {
  const [code, setCode] = useState(CODE_PREFIX);
  const [finding, setFinding] = useState(false);
  const [result, setResult] = useState(null);
  const verifier = useFunction(VERIFIER);
  const valid = CODE_PATTERN.test(code);

  async function submit(e) {
    e.preventDefault();
    if (!valid) {
      toast.error('Order numbers look like GC-7K2QXM');
      return;
    }
    setResult(null);
    setFinding(true);
    let found;
    try {
      const res = await sdk.table('orders_c').select(['Name']).where(eq('Name', code)).limit(1).fetch();
      found = res?.data?.[0];
    } catch {
      found = undefined;
    } finally {
      setFinding(false);
    }
    if (!found) {
      toast.error(`No order ${code}`);
      return;
    }
    const res = await verifier.invoke({ mode: 'verify', orderId: found.Id });
    if (res) setResult(res);
  }

  const busy = finding || verifier.loading;

  return (
    <Panel title="Check an order" icon="ScanSearch" bodyClassName="py-4" className="mt-6">
      <p className="text-sm text-muted-foreground">
        Runs the same check a buyer gets, on the line saved on any order, with the match signals shown. A pass or a
        failed sign-in updates that order's Verified badge.
      </p>
      <form onSubmit={submit} className="mt-3 flex flex-col gap-2 sm:flex-row">
        <label htmlFor="verify-code" className="sr-only">
          Order number
        </label>
        <Input
          id="verify-code"
          value={code}
          onChange={(e) => setCode(asOrderCode(e.target.value))}
          placeholder="GC-7K2QXM"
          autoComplete="off"
          spellCheck={false}
          className="font-mono uppercase sm:w-44"
        />
        <Button type="submit" disabled={!valid || busy}>
          <Busy
            busy={busy}
            label={
              <>
                <ApperIcon name="ShieldCheck" size={16} />
                Verify line
              </>
            }
            busyLabel="Checking…"
          />
        </Button>
      </form>
      {result && (
        <div className="mt-4">
          <VerifierResult result={result} />
        </div>
      )}
    </Panel>
  );
}

export default function References() {
  const refs = useFetch(loadReferences, []);
  const list = refs.data ?? [];
  const [captureOpen, setCaptureOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [target, setTarget] = useState(null);

  function openCapture(r) {
    setTarget(r);
    setCaptureOpen(true);
  }

  function openEdit(r) {
    setTarget(r);
    setEditOpen(true);
  }

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Store"
        title="IPTV Verifier"
        description="Buyers check their line from their account and see Genuine Ghost Cable or Not recognised. Their line is compared against every reference saved here."
        actions={
          <Button onClick={() => openCapture(null)}>
            <ApperIcon name="Plus" size={16} />
            Add reference
          </Button>
        }
      />

      <Panel
        title="Reference lines"
        icon="Fingerprint"
        bodyClassName="py-4"
        actions={
          <span className="text-xs text-muted-foreground tabular-nums">
            {list.length} saved
          </span>
        }
      >
        <ListBody
          loading={refs.loading}
          data={refs.data}
          error={refs.error}
          run={refs.run}
          rows={3}
          isEmpty={!list.length}
          empty={{
            icon: 'Fingerprint',
            title: 'No references yet',
            description: 'Add a known-good line so buyers can pass the genuine check. Add as many as you run: per plan, per server, backups.',
            action: () => openCapture(null),
            actionLabel: 'Add reference',
          }}
        >
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {list.map((r) => (
              <ReferenceCard
                key={r.Id}
                reference={r}
                onCapture={() => openCapture(r)}
                onEdit={() => openEdit(r)}
                onDeleted={() => refs.run()}
              />
            ))}
          </div>
        </ListBody>
        <p className="mt-4 text-xs text-muted-foreground">
          Saved per reference: hashed server and logo hosts (including the extra links), 30 sample channel and category IDs,
          the first few category names, naming style and channel counts. No usernames or passwords. Names and links are
          visible to the team only; buyers only ever see Genuine Ghost Cable or Not recognised.
        </p>
      </Panel>

      <CheckOrder />

      <CaptureDialog reference={target} open={captureOpen} onOpenChange={setCaptureOpen} onSaved={() => refs.run()} />
      <EditDialog reference={target} open={editOpen} onOpenChange={setEditOpen} onSaved={() => refs.run()} />
    </div>
  );
}
